import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, ChangeList, ConsentSwitch, OptionGrid, Sheet, Skeleton } from '@/ds'
import { useLoadedStay } from '@/app/stay/StayProvider'
import { useTextLarge } from '@/app/stay/useTextLarge'
import { isLanguage, LANGUAGES, saveLanguage } from '@/i18n'
import type { Language } from '@/i18n'
import { asyncValue, useAsync } from '@/lib/useAsync'
import { notificacionesService } from '@/services/notificaciones'
import { SectionError } from '../Estancia/sections/SectionError'
import { BackLink } from '../Pagos/shared'
import { useLang } from '../Reserva/shared'

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.charAt(0).toLocaleUpperCase())
    .join('')

/**
 * 8.12 · Perfil (Cierre.dc.html profile, Bloque 7 §7d): sin contraseña; idioma inicial del dispositivo,
 * modificable desde aquí, y texto grande en el cliente.
 */
export function Perfil() {
  const { t } = useLang()
  const stay = useLoadedStay()
  const [profile, reloadProfile] = useAsync(`perfil:${stay.guestCode}`, (signal) => notificacionesService.getProfile(stay, signal))
  const p = asyncValue(profile)
  return (
    <main className="page">
      <div className="pagos__head">
        <BackLink to="/" label={t('v2.ds.nav.estancia')} />
        <h1>{t('v2.perfil.titulo')}</h1>
        <p className="page__lead">{t('v2.perfil.lead')}</p>
      </div>
      {profile.kind === 'loading' && (
        <div aria-busy="true">
          <Skeleton height={80} />
        </div>
      )}
      {profile.kind === 'error' && <SectionError code={profile.code} onRetry={reloadProfile} />}
      {p && (
        <Card>
          {p.name ? (
            <div className="cierre__identity">
              <span className="cierre__avatar" aria-hidden="true">
                {initials(p.name)}
              </span>
              <span className="cierre__identity-text">
                <h2 className="cierre__card-title">{p.name}</h2>
                {(p.email || p.phone) && <span className="cierre__note tnum">{[p.email, p.phone].filter(Boolean).join(' · ')}</span>}
              </span>
            </div>
          ) : (
            <p className="cierre__text">{t('v2.perfil.sinTitular')}</p>
          )}
        </Card>
      )}
      <Settings />
    </main>
  )
}

function Settings() {
  const { t, lang } = useLang()
  const { i18n } = useTranslation()
  const [textLarge, setTextLarge] = useTextLarge()
  const [sheet, setSheet] = useState(false)
  const current: Language = isLanguage(lang) ? lang : 'en'
  const [choice, setChoice] = useState<Language>(current)
  const languageName = t(`v2.perfil.idiomas.${current}`)
  return (
    <div className="cierre__stack">
      <ChangeList items={[{ id: 'idioma', title: t('v2.perfil.idioma'), condition: languageName, onClick: () => setSheet(true) }]} />
      <Card>
        <ConsentSwitch checked={textLarge} onChange={setTextLarge} title={t('v2.perfil.textoGrande')} description={t('v2.perfil.textoGrandeTexto')} />
      </Card>
      <Sheet
        open={sheet}
        title={t('v2.perfil.elegirIdioma')}
        confirmLabel={t('v2.ds.sheet.confirmar')}
        onCancel={() => setSheet(false)}
        onConfirm={() => {
          setSheet(false)
          if (choice !== current) {
            saveLanguage(choice)
            void i18n.changeLanguage(choice)
          }
        }}
      >
        <OptionGrid label={t('v2.perfil.idioma')} options={LANGUAGES.map((language) => ({ id: language, label: t(`v2.perfil.idiomas.${language}`) }))} value={choice} onChange={(id) => setChoice(id as Language)} columns={2} />
      </Sheet>
    </div>
  )
}

